import {
  Component,
  ChangeDetectionStrategy,
  signal,
  computed,
  inject,
  ViewChild,
  AfterViewInit,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { environment } from 'src/environments/environment';

// Kendo Components
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { NotificationService } from '@progress/kendo-angular-notification';
import { TooltipsModule } from '@progress/kendo-angular-tooltip';

// Core Components
import { DatatableComponent } from 'src/app/core/components/datatable/datatable.component';
import { PopupHostComponent } from 'src/app/core/components/popup-host.component';

// Models & Services
import { AuditFilterState, IAuditEvent, IFilterChoice, IOrganization } from 'src/app/core/models/audit-event';
import { AuditLogService } from 'src/app/core/services/audit-log.service';
import { ReportJobService, ReportJobConfig } from 'src/app/core/services/reporting/report-job.service';

// Child Components
import { AuditDetailComponent } from './components/audit-detail/audit-detail.component';
import { AuditLogFiltersComponent } from './components/audit-log-filters/audit-log-filters.component';

@Component({
  selector: 'app-audit-log',
  standalone: true,
  imports: [CommonModule, ButtonsModule, TooltipsModule, DatatableComponent, AuditLogFiltersComponent],
  templateUrl: './audit-log.component.html',
  styleUrl: './audit-log.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuditLogComponent extends PopupHostComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('auditTable') auditTable!: DatatableComponent;

  private auditLogService = inject(AuditLogService);
  private reportJobService = inject(ReportJobService);
  private notificationService = inject(NotificationService);
  private route = inject(ActivatedRoute);

  // Loading state
  public isLoading = signal<boolean>(true);
  public isExporting = signal<boolean>(false);

  // Report template ID loaded from API
  private auditLogTemplateId = signal<number | null>(null);

  // Current filter state (set on apply, cleared on reset)
  public currentFilterState = signal<AuditFilterState | null>(null);

  // Column search values from DataTable
  public columnSearch: Record<string, string> = {};

  // Export gating: requires at least one org selected
  public canExport = computed(() => {
    const state = this.currentFilterState();
    return !!state && state.organizationIds.length > 0;
  });

  // Count of active non-org filters
  private activeFilterCount = computed(() => {
    const state = this.currentFilterState();
    if (!state) return 0;
    let count = 0;
    if (state.outcome) count++;
    if (state.action) count++;
    if (state.eventType) count++;
    if (state.userType) count++;
    if (state.startDate) count++;
    if (state.endDate) count++;
    if (this.columnSearch['user_email']) count++;
    if (this.columnSearch['entity_type']) count++;
    return count;
  });

  // Tooltip for export button
  public exportTooltip = computed(() => {
    const state = this.currentFilterState();
    if (!state || state.organizationIds.length === 0) {
      return 'Select at least one organization and click Apply Filters to enable the export.';
    }
    const orgCount = state.organizationIds.length;
    const filterCount = this.activeFilterCount();
    const orgText = `${orgCount} org${orgCount !== 1 ? 's' : ''}`;
    const filterText = filterCount > 0 ? `, ${filterCount} filter${filterCount !== 1 ? 's' : ''} applied` : '';
    return `Export ${orgText}${filterText}`;
  });

  // Datatable configuration
  public datatableUrl = `${environment.apiUrl}/v1/audit/events`;

  // Filter choices (loaded from API)
  public organizations = signal<IOrganization[]>([]);
  public isLoadingOrganizations = signal<boolean>(false);
  public actions = signal<IFilterChoice[]>([]);
  public outcomes = signal<IFilterChoice[]>([]);
  public eventTypes = signal<IFilterChoice[]>([]);
  public userTypes = signal<IFilterChoice[]>([]);

  // Detail panel state
  public selectedEvent = signal<IAuditEvent | null>(null);

  // Datatable columns configuration
  public datatableColumns = [
    {
      data: 'event_time',
      name: 'event_time',
      title: 'Time',
      type: 'string',
      render: (data: string) => {
        if (!data) return '<span class="text-muted">—</span>';
        const date = new Date(data);
        return `
          <div class="small">
            <div>${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
            <div class="text-muted">${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</div>
          </div>
        `;
      },
    },
    {
      data: 'organization_name',
      name: 'organization_name',
      title: 'Organization',
      type: 'string',
    },
    {
      data: 'user_email',
      name: 'user_email',
      title: 'User',
      type: 'string',
      render: (data: string, _type: any, row: IAuditEvent) => {
        if (!data) return `<span class="text-muted fst-italic">${row.user_type}</span>`;
        return `
          <div>
            <div class="small">${data}</div>
            <div class="text-muted small">${row.user_type}</div>
          </div>
        `;
      },
    },
    {
      data: 'action',
      name: 'action',
      title: 'Action',
      type: 'string',
      render: (data: string) => {
        const actionMap: Record<string, string> = {
          create: 'create',
          update: 'update',
          delete: 'delete',
          login: 'login',
          logout: 'logout',
        };
        const actionClass = actionMap[data] || 'default';
        return `<span class="badge badge-action--${actionClass}">${data}</span>`;
      },
    },
    {
      data: 'event_type',
      name: 'event_type',
      title: 'Event Type',
      type: 'string',
      render: (data: string) => {
        return `<span class="badge badge-event-type">${data}</span>`;
      },
    },
    {
      data: 'entity_type',
      name: 'entity_type',
      title: 'Entity',
      type: 'string',
      render: (data: string, _type: any, row: IAuditEvent) => {
        if (!data) return '<span class="text-muted">—</span>';
        return `
          <div class="small">
            <div>${data}</div>
            <div class="text-muted">${row.entity_id || ''}</div>
          </div>
        `;
      },
    },
    {
      data: 'outcome',
      name: 'outcome',
      title: 'Outcome',
      type: 'string',
      render: (data: string) => {
        const normalized = data?.toLowerCase();
        const displayValue = data?.replace('_', ' ') ?? '';
        const outcomeMap: Record<string, string> = {
          success: 'success',
          validation_failed: 'warning',
          denied: 'danger',
          error: 'danger',
        };
        const outcomeClass = outcomeMap[normalized] || 'success';
        const icon = normalized === 'success' ? '✓' : normalized === 'denied' ? '⊘' : '⚠';
        return `<span class="badge badge-outcome--${outcomeClass}">${icon} ${displayValue}</span>`;
      },
    },
    {
      data: 'change_summary',
      name: 'change_summary',
      title: 'Changes',
      type: 'string',
      orderable: false,
      render: (data: string, _type: any, row: IAuditEvent) => {
        if (!data && !row.diff_json) return '<span class="text-muted">—</span>';
        const hasDiff = row.tier === 2 && row.diff_json;
        const text = data || (hasDiff ? 'View changes' : '—');
        return `<span class="small ${hasDiff ? 'text-primary' : ''}">${text}</span>`;
      },
    },
    {
      data: 'id',
      name: 'actions',
      title: '',
      type: 'actions',
      orderable: false,
      width: 80,
      render: (data: string) => {
        return `
          <button class="btn btn-sm btn-outline-primary action-view-details" data-id="${data}" title="View Details"> Details
          </button>
        `;
      },
    },
  ];

  ngOnInit(): void {
    this.loadFilterChoices();
    this.loadReportTemplateId();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.setupDatatableEventHandlers();
    }, 500);
  }

  override ngOnDestroy(): void {
    $(document).off('click', '.action-view-details');
    super.ngOnDestroy();
  }

  /**
   * Load filter choices from API.
   *
   * Organizations are requested first since they're the heaviest call —
   * kicking them off early gives the browser the most time to have them
   * ready by the time the user clicks the dropdown.
   */
  private loadFilterChoices(): void {
    this.isLoadingOrganizations.set(true);
    this.auditLogService.searchOrganizations().subscribe({
      next: (orgs) => {
        this.organizations.set(orgs);
        this.isLoadingOrganizations.set(false);
      },
      error: (err) => {
        console.error('Failed to load organizations:', err);
        this.isLoadingOrganizations.set(false);
      },
    });

    this.auditLogService.getChoices().subscribe({
      next: (choices) => {
        this.actions.set(choices.actions);
        this.outcomes.set(choices.outcomes);
        this.eventTypes.set(choices.event_types);
        this.userTypes.set(choices.user_types);
        this.isLoading.set(false);
      },
      error: (err) => {
        console.error('Failed to load filter choices:', err);
        this.isLoading.set(false);
      },
    });
  }

  /**
   * Load the audit-log-report template ID from the reporting API
   */
  private loadReportTemplateId(): void {
    this.auditLogService.loadReportTemplateId().subscribe({
      next: (id) => this.auditLogTemplateId.set(id),
      error: (err) => console.error('Failed to load audit log report template:', err),
    });
  }

  /**
   * Setup datatable event handlers
   */
  private setupDatatableEventHandlers(): void {
    $(document).off('click', '.action-view-details');
    $(document).on('click', '.action-view-details', (event) => {
      const eventId = $(event.currentTarget).data('id');
      this.viewEventDetails(eventId);
    });
  }

  /**
   * Handle column search events from DataTable
   */
  public onColumnSearch(columnName: string, value: string): void {
    if (value) {
      this.columnSearch[columnName] = value;
    } else {
      delete this.columnSearch[columnName];
    }
  }

  /**
   * Handle organization search from the filter component (server-side).
   */
  public onOrganizationSearch(query: string): void {
    // No loading spinner here — PrimeNG's client-side filter gives instant
    // feedback on the currently loaded options while the server search
    // updates them silently in the background.
    this.auditLogService.searchOrganizations(query).subscribe({
      next: (orgs) => this.organizations.set(orgs),
      error: (err) => console.error('Failed to search organizations:', err),
    });
  }

  /**
   * Handle filter apply event from child component
   */
  public onApplyFilters(state: AuditFilterState): void {
    this.currentFilterState.set(state);
    const filters: Record<string, string> = {};
    const odataConditions: string[] = [];

    if (state.organizationIds.length > 0) {
      const orgCondition = state.organizationIds
      .map(id => `organization_id eq ${id}`)
      .join(' or ');

      odataConditions.push(`(${orgCondition})`);
    }
    if (state.outcome) {
      odataConditions.push(`outcome eq '${state.outcome}'`);
    }
    if (state.action) {
      odataConditions.push(`action eq '${state.action}'`);
    }
    if (state.eventType) {
      odataConditions.push(`event_type eq '${state.eventType}'`);
    }
    if (state.userType) {
      odataConditions.push(`user_type eq '${state.userType}'`);
    }
    if (state.startDate) {
      odataConditions.push(`event_time ge '${state.startDate.toISOString()}'`);
    }
    if (state.endDate) {
      odataConditions.push(`event_time le '${state.endDate.toISOString()}'`);
    }

    if (odataConditions.length > 0) {
      filters['$filter'] = odataConditions.join(' and ');
    }

    if (this.auditTable) {
      this.auditTable.setFilters(filters);
    }
  }

  /**
   * Handle filter reset event from child component
   */
  public onResetFilters(): void {
    this.currentFilterState.set(null);
    if (this.auditTable) {
      this.auditTable.setFilters({});
    }
  }

  /**
   * View event details in slide-in panel
   */
  public viewEventDetails(eventId: string): void {
    this.auditLogService.getAuditEvent(eventId).subscribe({
      next: (event) => {
        this.selectedEvent.set(event);
        this.openDetailPanel();
      },
      error: (err) => console.error('Failed to load event details:', err),
    });
  }

  /**
   * Open detail slide-in panel
   */
  private openDetailPanel(): void {
    const options = {
      animate: { type: 'slide', direction: 'left', duration: 200 },
      anchorAlign: { horizontal: 'right', vertical: 'top' },
      popupAlign: { horizontal: 'right', vertical: 'top' },
    };

    this.openPopup(this.bodyElement, AuditDetailComponent, options);

    const detailComponent = this.popupRef?.content.instance as AuditDetailComponent;

    if (detailComponent) {
      detailComponent.event.set(this.selectedEvent());
      detailComponent.close.subscribe(() => {
        this.closePopup();
        this.selectedEvent.set(null);
      });
    }
  }

  /**
   * Trigger audit log export via the reporting system
   */
  public triggerExport(): void {
    const state = this.currentFilterState();
    if (!state || state.organizationIds.length === 0) {
      this.notificationService.show({
        content: 'Please select at least one organization before exporting.',
        type: { style: 'warning', icon: true },
        position: { horizontal: 'right', vertical: 'top' },
        animation: { type: 'fade', duration: 400 },
        closable: true,
        hideAfter: 5000,
      });
      return;
    }

    const templateId = this.auditLogTemplateId();
    if (!templateId) {
      this.notificationService.show({
        content: 'Audit log report template not available. Please try again later.',
        type: { style: 'error', icon: true },
        position: { horizontal: 'right', vertical: 'top' },
        animation: { type: 'fade', duration: 400 },
        closable: true,
        hideAfter: 5000,
      });
      return;
    }

    this.isExporting.set(true);

    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    const rawStart = state.startDate ?? thirtyDaysAgo;
    const rawEnd = state.endDate ?? now;
    const startUTC = new Date(Date.UTC(rawStart.getFullYear(), rawStart.getMonth(), rawStart.getDate(), 0, 0, 0));
    const endUTC = new Date(Date.UTC(rawEnd.getFullYear(), rawEnd.getMonth(), rawEnd.getDate(), 23, 59, 59, 999));

    const config: ReportJobConfig = {
      organizations: state.organizationIds,
      // Use the navbar org (from URL) so the export appears in Reports
      // History under the user's current navbar context. Fall back to the
      // first selected filter org if the URL param is missing.
      primary_organization_id: Number(this.route.snapshot.queryParamMap.get('organizationId')) || state.organizationIds[0],
      aggregate_report: true,
      report_template_id: templateId,
      start_date: formatDate(startUTC, 'yyyy-MM-dd HH:mm:ss', 'en-US', 'UTC'),
      end_date: formatDate(endUTC, 'yyyy-MM-dd HH:mm:ss', 'en-US', 'UTC'),
      delivery_method: 'download',
      report_params: {
        outcome: state.outcome || null,
        action: state.action || null,
        event_type: state.eventType || null,
        user_type: state.userType || null,
        user_email: this.columnSearch['user_email'] || null,
        entity_type: this.columnSearch['entity_type'] || null,
      },
    };

    this.reportJobService.createReportJob(config).subscribe({
      next: () => {
        this.isExporting.set(false);
        this.notificationService.show({
          content:
            'Audit log export has been triggered. You will be able to find the report in the history tab once the report has been generated.',
          type: { style: 'success', icon: true },
          position: { horizontal: 'right', vertical: 'top' },
          animation: { type: 'fade', duration: 400 },
          closable: true,
          hideAfter: 5000,
        });
      },
      error: () => {
        this.isExporting.set(false);
        this.notificationService.show({
          content: 'Failed to export audit log. Please try again.',
          type: { style: 'error', icon: true },
          position: { horizontal: 'right', vertical: 'top' },
          animation: { type: 'fade', duration: 400 },
          closable: true,
          hideAfter: 5000,
        });
      },
    });
  }
}
